import React from 'react';
import { Link } from 'react-router-dom';
import { Youtube, Twitter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../hooks/useContent';

const Footer = () => {
  const { t, language } = useLanguage();
  const { pick } = useContent('footer', {
    tagline_en: t.footer.tagline, tagline_el: t.footer.tagline,
    disclaimer_en: t.footer.disclaimer, disclaimer_el: t.footer.disclaimer,
    copyright_en: t.footer.copyright, copyright_el: t.footer.copyright,
  });

  // Legal/compliance routes auto-switch by language.
  // Greek site → /*-gr,  English site → /* (no suffix).
  const suffix = language === 'el' ? '-gr' : '';
  const legalLabels = language === 'el'
    ? {
        terms: 'Όροι Χρήσης',
        privacy: 'Πολιτική Απορρήτου',
        refund: 'Πολιτική Ακυρώσεων & Επιστροφών',
        disclaimer: 'Αποποίηση Ευθύνης',
        contact: 'Επικοινωνία',
      }
    : {
        terms: 'Terms of Service',
        privacy: 'Privacy Policy',
        refund: 'Refund & Cancellation Policy',
        disclaimer: 'Disclaimer',
        contact: 'Contact',
      };

  const footerLinks = {
    product: [
      { name: t.footer.links.pricing, href: '/#pricing' },
      { name: t.footer.links.algorithms, href: '/features/algorithms' },
      { name: t.footer.links.riskBehavior, href: '/features/risk-behavior' }
    ],
    resources: [
      { name: t.footer.links.docs, href: '/docs' },
      { name: t.footer.links.about, href: '#' },
      { name: t.footer.links.community, href: '/community' },
      { name: t.footer.links.affiliates, href: '#' }
    ],
    legal: [
      { name: legalLabels.terms, to: `/terms${suffix}`, testid: 'footer-terms' },
      { name: legalLabels.privacy, to: `/privacy${suffix}`, testid: 'footer-privacy' },
      { name: legalLabels.refund, to: `/refund-policy${suffix}`, testid: 'footer-refund' },
      { name: legalLabels.disclaimer, to: `/disclaimer${suffix}`, testid: 'footer-disclaimer' },
      { name: legalLabels.contact, to: `/contact${suffix}`, testid: 'footer-contact' },
    ],
  };

  return (
    <footer className="bg-[#050811] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Logo and Social */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center space-x-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_heliophant-algo/artifacts/lhe7g4r4_logoHELIOPHANT.png" 
                alt="Heliophant" 
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold text-white">Heliophant</span>
            </div>
            <p className="text-white/60 text-sm mb-4">
              {pick('tagline')}
            </p>
            <div className="flex space-x-4">
              <a
                href="#"
                className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors"
                aria-label="YouTube"
              >
                <Youtube size={20} className="text-white/70" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Twitter"
              >
                <Twitter size={20} className="text-white/70" />
              </a>
            </div>
          </div>
          
          {/* Product Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.footer.product}</h3>
            <ul className="space-y-2">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-white/60 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Resources Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t.footer.resources}</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-white/60 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Legal / Compliance Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{language === 'el' ? 'Νομικά' : 'Legal'}</h3>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={link.testid}
                    className="text-white/60 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Disclaimer */}
        <div className="border-t border-white/5 pt-8">
          <p className="text-white/60 text-xs leading-relaxed mb-4">
            {pick('disclaimer')}
          </p>
          <p className="text-white/60 text-xs mb-6">
            {pick('copyright')}
            <span className="mx-2">·</span>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('heliophant:reopen-cookie-consent'))}
              className="text-white/60 hover:text-white underline underline-offset-2 transition-colors"
              data-testid="footer-cookie-settings"
            >
              {language === 'el' ? 'Ρυθμίσεις cookies' : 'Cookie settings'}
            </button>
          </p>
          
          {/* ESPA Logo */}
          <div className="flex justify-center mt-8">
            <a 
              href="https://greece20.gov.gr/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block transition-opacity hover:opacity-80"
            >
              <img 
                src="https://greece20.gov.gr/wp-content/uploads/2023/03/greece20-NextGeneration_logo-el-01.png"
                alt="Greece 2.0 - NextGeneration EU"
                className="h-12 md:h-16 w-auto"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
